import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";

import { faPaperPlaneTop, faStopCircle } from "@fortawesome/pro-solid-svg-icons";

import {
  GeneratedImageInfo,
  ImageGenerationRequest,
  ImageGenerationResponse,
  ImageGenerationSignalRMessage,
  SignalREventNames,
} from "../../../../@api";

import { ActionButton, fieldNonexistentOrMatchingFilter } from "../../../components/Shared";
import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { IProgressColumn } from "../../../components/Shared/ProgressHandler/ProgressHandler";
import { useProgressHandler } from "../../../components/Shared/ProgressHandler/useProgressHandler";
import { translationKeys } from "../../../models/app-translation-keys";
import { useImageGenerationService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";
import { useIsImageGenDisabled } from "../Hooks/useIsImageGenDisabled";
import { ImageBackgroundControl } from "../ImageBackgroundControl/ImageBackgroundControl";
import { ImageCountControl } from "../ImageCountControl/ImageCountControl";
import { ImageGenDisclaimer } from "../ImageGenDisclaimer/ImageGenDisclaimer";
import { ImageQualityControl } from "../ImageQualityControl/ImageQualityControl";
import { ImageResolutionControl } from "../ImageResolutionControl/ImageResolutionControl";

import "./ImageGenerationPrompts.scss";

import { observer } from "mobx-react-lite";

import { applicationConfig } from "../../../../environmentConfiguration";

interface IImageGenPromptProps {
  selectedSuggestion: string;
  maxCharCount?: number;
  cancellingGeneration: boolean;
  setCancellingGeneration: (cancelling: boolean) => void;
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;

const isSignalRResponse = (
  signalRResponse: ImageGenerationSignalRMessage | ImageGenerationResponse,
): signalRResponse is ImageGenerationSignalRMessage => {
  return (
    "processId" in signalRResponse ||
    "userId" in signalRResponse ||
    "result" in signalRResponse ||
    "error" in signalRResponse ||
    "progress" in signalRResponse
  );
};

const extractImages = (
  signalRResponse?: ImageGenerationSignalRMessage | ImageGenerationResponse,
): GeneratedImageInfo[] => {
  if (!signalRResponse) {
    return [];
  }

  if (isSignalRResponse(signalRResponse)) {
    return signalRResponse.result.images ?? [];
  }

  return Array.isArray(signalRResponse.images) ? signalRResponse.images : [];
};

const extractImageIds = (signalRResponse?: ImageGenerationSignalRMessage | ImageGenerationResponse): string[] => {
  const images = extractImages(signalRResponse);

  return images.map(image => image.imageId);
};

export const ImageGenerationPrompt: FunctionComponent<IImageGenPromptProps> = observer(
  ({ selectedSuggestion, maxCharCount, cancellingGeneration, setCancellingGeneration }: IImageGenPromptProps) => {
    const { t } = useTranslation();
    const imageGenService = useImageGenerationService();
    const { imageGenerationStore, budgetStore, globalErrorsStore } = useStore();

    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
    const [disableImageGenTooltipKey, setDisableImageGenTooltipKey] = useState<string | undefined>();
    const [isImageGenerationActive, setIsImageGenerationActive] = useState<boolean>(false);

    const isImageGenActivated =
      !applicationConfig.featureFlags.enableImageGenerationDisclaimer || isImageGenerationActive;

    const hasPromptErrors = globalErrorsStore.hasSpecificErrors([
      featureTranslationKeys.errors.invalidAiModel,
      featureTranslationKeys.errors.invalidImageCount,
      featureTranslationKeys.errors.invalidColor,
      featureTranslationKeys.errors.invalidResolution,
      featureTranslationKeys.errors.invalidQuality,
    ]);

    const isImageGenerationDisabled = useIsImageGenDisabled();

    const disabled = imageGenerationStore.isGeneratingImages || imageGenerationStore.loadingImageIds;

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const inputPromptWrapperRef = useRef<HTMLDivElement>(null);
    const errorToast = useRef<Toast | null>(null);
    const requestStartedRef = useRef(false);

    useEffect(() => {
      if (budgetStore.hasBudget === false) {
        setDisableImageGenTooltipKey(translationKeys.globalErrors.serviceUnavailable);
      } else {
        setDisableImageGenTooltipKey(undefined);
      }
    }, [budgetStore.hasBudget]);

    useEffect(() => {
      if (!selectedSuggestion) {
        return;
      }

      setIsInputFocused(true);
      handleChange(selectedSuggestion);
    }, [selectedSuggestion]);

    const activeProcessId = imageGenerationStore.activeProcess?.processId ?? null;

    const finalizeGeneration = () => {
      imageGenerationStore.setLoadingImageIds(false);
      imageGenerationStore.setGeneratingImages(false);
    };

    const handleSignalRSuccess = (response: ImageGenerationSignalRMessage) => {
      const imageIds = extractImageIds(response);

      if (!imageIds.length) {
        imageGenerationStore.setGeneratingImages(false);
        globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestFailed);
      } else {
        imageGenerationStore.setGeneratedImageIds(imageIds);
        finalizeGeneration();
      }
    };

    const handleSignalRError = (response: ImageGenerationSignalRMessage) => {
      imageGenerationStore.setGeneratedImageIds([]);
      imageGenerationStore.setGeneratingImages(false);
      console.error("[ImageGenerationPrompt] Image generation failed via SignalR", response);
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestFailed);
    };

    const cancelGeneration = async (unsetLoading?: boolean) => {
      if (cancellingGeneration) {
        return;
      }

      if (!activeProcessId) {
        finalizeGeneration();
        return;
      }

      try {
        imageGenerationStore.setLoadingImageIds(false);
        setCancellingGeneration(true);
        await imageGenService.cancelImageGeneration(activeProcessId);
        imageGenerationStore.resetActiveProcess();
      } catch (error) {
        console.error("[ImageGenerationPrompt] Failed to cancel image generation", error);
      } finally {
        finalizeGeneration();
        setCancellingGeneration(false);
      }
    };

    const startRequest = async () => {
      if (requestStartedRef.current) {
        return;
      }

      requestStartedRef.current = true;

      try {
        let payload: ImageGenerationRequest = {
          prompt: imageGenerationStore.prompt.trim(),
          modelId: imageGenerationStore.selectedAiModel?.modelId || "",
          imageCount: imageGenerationStore.imageCount || 1,
          imageSize: imageGenerationStore.imageResolution,
          imageQuality: imageGenerationStore.imageQuality,
        };

        if (imageGenerationStore.backgroundColor) {
          payload = {
            ...payload,
            backgroundColor: imageGenerationStore.backgroundColor,
          };
        }

        const response = await imageGenService.generateImages(payload);

        if (!response.processId) {
          globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestFailed);
          finalizeGeneration();
          requestStartedRef.current = false;
          return;
        }

        imageGenerationStore.setActiveProcessId(response.processId);
        imageGenerationStore.setGeneratingImages(true);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;

        if (status === 422) {
          globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestViolateContentPolicy);
        } else {
          globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestFailed);
        }

        console.error("[ImageGenerationPrompt] Failed to generate images", error);
        finalizeGeneration();
        requestStartedRef.current = false;
      }
    };

    const startRequestIfSignalRReady = () => {
      // SignalR connection established
    };

    // called to initiate the generation flow
    const startGeneration = () => {
      // #59966 prevent startRequest and display FE error if aiModel?.modelId is not selected
      if (!imageGenerationStore.selectedAiModel?.modelId) {
        globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidAiModel);
        return;
      } else {
        globalErrorsStore.removeError(featureTranslationKeys.errors.invalidAiModel);
      }

      requestStartedRef.current = false;
      imageGenerationStore.resetActiveProcess();
      imageGenerationStore.setGeneratingImages(false);
      imageGenerationStore.setLoadingImageIds(true);

      void startRequest();
    };

    const onSubmitClick = () => {
      if (!isImageGenActivated) {
        return;
      }

      // validation
      if (!imageGenerationStore.prompt?.trim().length) {
        errorToast.current?.show({
          severity: "warn",
          summary: t(featureTranslationKeys.toast.emptyPromptSummary),
          detail: t(featureTranslationKeys.toast.emptyPromptDetail),
        });

        return;
      }

      startGeneration();
    };

    // UI handlers
    const handleChange = (nextValue: string) => {
      if (maxCharCount && nextValue.length > maxCharCount) {
        imageGenerationStore.setPrompt(nextValue.slice(0, maxCharCount));
      } else {
        imageGenerationStore.setPrompt(nextValue);
      }

      if (imageGenerationStore.prompt.length) {
        focusTextArea();
      }
    };

    const handleKeyDown = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (ev.key === "Enter") {
        if (ev.shiftKey) {
          return;
        }

        if (!isImageGenActivated) {
          return;
        }

        ev.preventDefault();
        onSubmitClick();
      }
    };

    const focusTextArea = () => {
      setTimeout(() => {
        textAreaRef.current?.focus();
        textAreaRef.current?.click();
      }, 100);
    };

    const getTooltipTextForSendInputButton = (): string => {
      if (!imageGenerationStore.selectedAiModel) {
        return featureTranslationKeys.tooltips.sendPromptButtonDisabledNoModelSelected;
      }

      if (disabled) {
        return featureTranslationKeys.tooltips.sendPromptButtonDisabledPendingGeneration;
      }

      if (!imageGenerationStore.prompt?.length) {
        return featureTranslationKeys.tooltips.sendPromptButtonDisabledNoInput;
      }

      if (!isImageGenActivated) {
        return featureTranslationKeys.tooltips.imageGenerationInactive;
      }

      return featureTranslationKeys.tooltips.sendInput;
    };

    const progressColumns = useMemo<IProgressColumn[]>(() => {
      if (!imageGenerationStore.loadingImageIds || !activeProcessId) {
        return [] as IProgressColumn[];
      }

      const processIdFilter = fieldNonexistentOrMatchingFilter("processId", activeProcessId);

      return [
        {
          titleKey: featureTranslationKeys.actions.loadingAIImageGeneration,
          progress: 0,
          hubName: "imagegeneration",
          progressEndpoint: SignalREventNames.ImageGenerationProgress,
          successEndpoint: SignalREventNames.ImageGenerationSucceeded,
          errorEndpoint: SignalREventNames.ImageGenerationFailed,
          filter: [processIdFilter],
          successCallback: handleSignalRSuccess,
          errorCallback: handleSignalRError,
        },
      ];
    }, [imageGenerationStore.loadingImageIds, activeProcessId]);

    useProgressHandler({
      progresscolumns: progressColumns,
      cancelProgress: () => cancelGeneration(),
      connectionSetupCompleted: startRequestIfSignalRReady,
      enabled: imageGenerationStore.loadingImageIds && !!activeProcessId,
      stopConnectionsOnUnmount: false,
    });

    return (
      <div className="input-component imagegen-prompt">
        <TooltipWrapper
          targetRef={inputPromptWrapperRef}
          position="top"
          disabled={!isImageGenerationDisabled || !disableImageGenTooltipKey}
          content={disableImageGenTooltipKey && t(disableImageGenTooltipKey)}
        />

        <Toast ref={errorToast} position="top-center" />

        <div
          className={classNames("input-component-form", {
            disabled: isImageGenerationDisabled,
            focus: isInputFocused,
          })}
          ref={inputPromptWrapperRef}
        >
          <div className="imagegen-input-row">
            {applicationConfig.featureFlags.enableImageGenerationDisclaimer && (
              <ImageGenDisclaimer
                isActive={isImageGenerationActive}
                setIsActive={setIsImageGenerationActive}
                doNotShowAgainKey="doNotShowAgainImageGenDisclaimer"
                onApprove={focusTextArea}
              />
            )}

            <InputTextarea
              data-testid="image-generation-prompt-input"
              className="chat-textarea"
              ref={textAreaRef}
              value={imageGenerationStore.prompt}
              onKeyDown={handleKeyDown}
              onChange={e => handleChange(e.target.value as string)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              rows={3}
              disabled={isImageGenerationDisabled || disabled}
              autoResize={true}
              placeholder={t(featureTranslationKeys.promptPlaceholder)}
            />
          </div>

          <div className={classNames("imagegen-controls", { focus: isInputFocused })}>
            <div className="imagegen-meta">
              <TooltipWrapper
                targetRef=".char-counter"
                position="top"
                content={t(featureTranslationKeys.tooltips.charCounterTooltip)}
                disabled={isImageGenerationDisabled || disabled}
              />

              <div className="char-counter">
                {maxCharCount
                  ? `${imageGenerationStore.prompt.length}/${maxCharCount}`
                  : `${imageGenerationStore.prompt.length}`}
              </div>
            </div>

            <div className="imagegen-actions">
              <ImageCountControl
                imageCount={imageGenerationStore.imageCount}
                disabled={disabled}
                onChange={imageGenerationStore.setImageCount}
              />

              <ImageBackgroundControl
                backgroundColor={imageGenerationStore.backgroundColor}
                disabled={disabled}
                onChange={imageGenerationStore.setBackgroundColor}
                strictIsolationGuaranteed={false}
              />

              <ImageResolutionControl
                imageResolution={imageGenerationStore.imageResolution}
                disabled={disabled}
                onChange={imageGenerationStore.setImageResolution}
              />

              <ImageQualityControl
                imageQuality={imageGenerationStore.imageQuality}
                disabled={disabled}
                onChange={imageGenerationStore.setImageQuality}
              />

              {imageGenerationStore.loadingImageIds ? (
                <ActionButton
                  customClasses="cancel-generation-button"
                  icon={faStopCircle}
                  id="image-generation-cancel-button"
                  hideBackground={true}
                  tooltipDisabled={cancellingGeneration}
                  tooltip={t(featureTranslationKeys.tooltips.cancelImageGeneration)}
                  tooltipPosition="top"
                  buttonDisabled={cancellingGeneration || !activeProcessId}
                  onClick={() => cancelGeneration(true)}
                />
              ) : (
                <ActionButton
                  customClasses="send-prompt-button"
                  icon={faPaperPlaneTop}
                  id="image-generation-send-button"
                  hideBackground={true}
                  tooltip={t(getTooltipTextForSendInputButton())}
                  tooltipPosition="top"
                  buttonDisabled={
                    !imageGenerationStore.prompt.length ||
                    isImageGenerationDisabled ||
                    disabled ||
                    hasPromptErrors ||
                    !isImageGenActivated
                  }
                  onClick={onSubmitClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);
